/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable require-await */
import React, {
  useMemo,
  useEffect,
  useState,
  useCallback,
  forwardRef,
} from 'react';
import { Table, Form } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { Button, FloatingActionButton } from 'appkit-react';
import { v4 as uuidv4 } from 'uuid';
import useTheme from '@hooks/useTheme';
import { EditableCell } from './components';
import deleteIcon from '@assets/imgs/cancel.png';
import deleteLightIcon from '@assets/imgs/cancel_light.png';
import _ from 'lodash';
import {
  handleEditTableData,
  returnPureData,
  returnPureDataWithIdValue,
} from './dataProcessors';
import './index.scss';
import alert from '@components/alert';
// import moment from 'moment';
// import { sortableContainer, sortableElement } from 'react-sortable-hoc';
// import arrayMove from 'array-move';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';

import { CSS } from '@dnd-kit/utilities';

export const EditableContext = React.createContext(null);
/**
 * @name 可编辑表格组件
 * @author 吴迪 di.c.wu@pwc.com
 * @link https://4x.ant.design/components/table-cn/#API
 * @todo 暂时已知问题皆以解决，如有问题请联系作者或最后编辑的同学
 *
 * @desc 传入columns及dataSource即可轻松实现可编辑表格
 * @param tableProps 表格的属性（同antd-table）
 * 特殊说明：disabled+required 支持传入function，可以拿到当前行的数据
 * @param defaultColumns 默认的表头配置（同antd-table的表头配置）
 * @param defaultDataSource 默认表格数据
 * @param needDelete 是否需要删除icon，默认true
 * @param disabledActions 是否禁用所有的操作（如：新增、删除、编辑）
 * @param afterAddRowFunc 增加行之后进行的操作-比如默认值之类的.在这个方法里可以拿到新增的那一行数据进行处理
 * @param beforeEditRowFunc 编辑行成功之前进行的操作-比如清空值之类的.在这个方法里可以拿到新增的那一行数据进行处理
 * @param afterDeleteRowFunc 删除行之后的回调-这个方法可以拿到删除后的剩余行数据进行处理
 * @param checkIsSuccessfulDeleted 判断删除行是否成功
 * @param dataChange 增、删、改数据之后的回调
 * @param changeCustmizeData 自定义改数据
 * @param afterCancel cancel按钮后的回调
 * @param saveData 点击Generate保存数据之后的回调，可以拿到整个table的数据
 *
 * @version 0.1.1
 * @createdTime 2023-4-18
 * @lastModifiedTime 2023-4-28
 * @lastModifiedUser 吴迪
 */
const Index = (
  {
    edittingTableDataSource,
    setEdittingTableDataSource,
    tableProps = {},
    tableColumns = [],
    defaultDataSource = [],
    needDelete = true,
    disabledActions = false,
    afterAddRowFunc = (newItem) => {},
    afterDeleteRowFunc = (newItem) => {},
    checkIsSuccessfulDeleted = (newItem) => true,
    beforeEditRowFunc = (newItem) => {},
    dataChange = (newData) => {},
    saveData = (resData) => {},
    toggleEdit = () => {},
    changeCustmizeData = () => {},
    afterCancel = () => {},
  },
  editableRowRef,
) => {
  const { theme } = useTheme();
  const isLightMode = theme === 'light';

  const [form] = Form.useForm();
  // 控制view模式与edit模式的,true：edit模式，false：view模式
  const [isEditMode, setIsEditMode] = useState(false);
  // 初始数据
  const initData = defaultDataSource.map((item) => {
    const newItem = { ...item };
    Object.keys(item).forEach((key) => {
      if (key === 'id') {
        newItem.id = item.id;
      } else {
        newItem[`${key}_${item.id}`] = item[key];
      }
    });
    return newItem;
  });
  const itemWithId = (target) =>
    target.map((item) => {
      const newItem = { ...item };
      Object.keys(item).forEach((key) => {
        if (key === 'id') {
          newItem.id = item.id;
        } else {
          newItem[`${key}_${item.id}`] = item[key];
        }
      });
      return newItem;
    });
  const [tableDataSource, setTableDataSource] = useState(initData);
  const [undo, setUndo] = useState([]);
  const [redo, setRedo] = useState([]);
  // console.log('XXXXinitData', initData);
  // console.log('XXXXredo', redo);
  // console.log('XXXXtableDataSource[0]', tableDataSource[0]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Require 8px of movement before dragging starts
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const onDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = tableDataSource.findIndex((x) => x.id === active.id);
      const newIndex = tableDataSource.findIndex((x) => x.id === over.id);

      const newTableDataSource = handleArrayMove(
        tableDataSource,
        oldIndex,
        newIndex,
      );

      if (
        newTableDataSource[0].hierachy !== 0 ||
        !isCorrectHierachy(newTableDataSource)
      ) {
        return;
      }

      changeData(newTableDataSource);
    }
  };

  // 增删改都功过该方法改数据，改当前组件使用的data以及暴露给父组件的data
  const changeData = (data, needSetUndoRedo = true) => {
    if (
      needSetUndoRedo &&
      JSON.stringify(returnPureData(tableDataSource)) !==
        JSON.stringify(returnPureData(data)) &&
      tableDataSource.length > 0
    ) {
      setUndo([...undo, [...returnPureDataWithIdValue(tableDataSource)]]);
      setRedo([]);
    }

    const newData = returnPureData(data);
    dataChange(newData);

    // 这里使用者可能需要改这个data，所以改完之后我再进行一次数据处理
    setTableDataSource(
      handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newData,
      }),
    );
    setEdittingTableDataSource(newData);
  };

  const handleHierachyUp = async (currentId) => {
    const newTableData = JSON.parse(JSON.stringify(tableDataSource));
    const currentLineIndex = tableDataSource.findIndex(
      (d) => d.id === currentId,
    );

    for (let i = currentLineIndex + 1; i < newTableData.length; i++) {
      if (newTableData[i].hierachy <= newTableData[currentLineIndex].hierachy) {
        break;
      } else {
        newTableData[i].hierachy--;
      }
    }

    newTableData[currentLineIndex].hierachy--;

    changeData(
      handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newTableData,
      }),
    );
  };

  const isCorrectHierachy = (newTableDataSource) => {
    let result = true;

    for (let i = newTableDataSource.length - 1; i > 0; i--) {
      const targetElement = newTableDataSource[i];
      let hasParentNode = false;
      let tempTopHierachy = targetElement.hierachy;
      for (let j = i - 1; j >= 0; j--) {
        const currentHierachy = newTableDataSource[j].hierachy;
        tempTopHierachy =
          currentHierachy < tempTopHierachy ? currentHierachy : tempTopHierachy;
        if (
          currentHierachy === targetElement.hierachy - 1 &&
          tempTopHierachy === currentHierachy
        ) {
          hasParentNode = true;
          break;
        }
      }
      if (!hasParentNode) {
        result = false;
        break;
      }
    }

    return result;
  };

  const canHierachyBeDown = (currentId) => {
    const newTableDataSource = JSON.parse(JSON.stringify(tableDataSource));
    const currentLineIndex = tableDataSource.findIndex(
      (d) => d.id === currentId,
    );
    newTableDataSource[currentLineIndex].hierachy++;

    if (currentLineIndex === 0) {
      return false;
    }

    return isCorrectHierachy(newTableDataSource);
  };

  const handleHierachyDown = async (currentId) => {
    const newTableData = JSON.parse(JSON.stringify(tableDataSource));
    const currentLineIndex = tableDataSource.findIndex(
      (d) => d.id === currentId,
    );
    newTableData[currentLineIndex].hierachy++;

    changeData(
      handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newTableData,
      }),
    );
  };

  const handleAdd = async (pattern, currentId) => {
    let newTableData = [];

    const newData = {
      id: uuidv4(),
    };

    const currentLine = tableDataSource.find((d) => d.id === currentId);
    const currentLineIndex = tableDataSource.findIndex(
      (d) => d.id === currentId,
    );

    let leftArray = [];
    let rightArray = [];

    let splitIndex;
    let nextHigherHierachyIndex;

    switch (pattern) {
      case 'Add Child':
        newData.hierachy = currentLine.hierachy + 1;

        break;
      case 'Add Parent Sibling':
        newData.hierachy = currentLine.hierachy - 1;

        break;
      case 'Add Sibling':
        newData.hierachy = currentLine.hierachy;

        break;

      case 'Add Top':
        newData.name = 'Board';
        newData.type = 'Organization';
        newData.title = 'Board';
        newData.hierachy = 0;
      default:
        break;
    }

    nextHigherHierachyIndex = tableDataSource.findIndex(
      (d, i, a) => i > currentLineIndex && d.hierachy < newData.hierachy,
    );
    splitIndex =
      nextHigherHierachyIndex === -1
        ? tableDataSource.length
        : nextHigherHierachyIndex;

    leftArray = tableDataSource.slice(0, splitIndex);
    rightArray = tableDataSource.slice(splitIndex);

    newTableData = [...leftArray, newData, ...rightArray];

    afterAddRowFunc(newData);
    changeData(
      handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newTableData,
      }),
    );
  };

  useEffect(() => {
    if (tableDataSource.length === 0 && isEditMode) {
      handleAdd('Add Top');
    }
  }, [tableDataSource, isEditMode]);

  // 删除
  const handleDelete = async (currentId) => {
    if (checkIsSuccessfulDeleted(currentId)) {
      const targetLine = tableDataSource.find((d) => d.id === currentId);
      const targetLineIndex = tableDataSource.findIndex(
        (d) => d.id === currentId,
      );
      let lastDeleteLineIndex = targetLineIndex;
      for (let i = targetLineIndex + 1; i < tableDataSource.length; i++) {
        const element = tableDataSource[i];
        if (element.hierachy <= targetLine.hierachy) {
          break;
        } else {
          lastDeleteLineIndex = i;
        }
      }

      const leftArray = tableDataSource.slice(0, targetLineIndex);
      const rightArray = tableDataSource.slice(lastDeleteLineIndex + 1);

      const newData = [...leftArray, ...rightArray];

      afterDeleteRowFunc(newData);
      changeData(
        handleEditTableData({
          formVal: form.getFieldsValue(),
          tableData: newData,
        }),
      );
    }
  };

  // 保存数据 - 失焦即调
  const handleSave = useCallback(
    async (row) => {
      const newData = [...tableDataSource];
      const index = newData.findIndex((item) => row.id === item.id);
      const curItem = newData[index];
      console.log('保存curItem：', curItem);
      if (_.isEqual(row, curItem)) {
        return console.log('没有变化');
      }
      newData.splice(index, 1, {
        ...curItem,
        ...row,
      });
      const resData = handleEditTableData({
        formVal: form.getFieldsValue(),
        tableData: newData,
      });
      // 在这里拿到处理过的数据之后这个数据才是正确的，所以在这里调before
      // 否则使用的地方拿到的数据是有问题的
      beforeEditRowFunc(resData[index]);
      changeData(resData);
    },
    [tableDataSource],
  );

  // 获取禁用的类名 - 如果不是禁用的就返回空字符串
  const getDisabledClassName = useMemo(
    () => (disabledActions ? 'disabled_style' : ''),
    [disabledActions],
  );

  useEffect(() => {
    toggleEdit(isEditMode);
  }, [isEditMode, toggleEdit]);

  // 判断是否可操作
  const actionWrapper = useCallback(
    (action) => {
      if (disabledActions) {
        return;
      }
      action && action();
    },
    [disabledActions],
  );

  // 处理表头数据-追加 序号列/删除列 且处理可编辑逻辑
  const columns = useMemo(() => {
    const newColumns = [...tableColumns];
    if (needDelete && isEditMode) {
      newColumns.push({
        title: '',
        dataIndex: 'operation',
        width: 40,
        render: (_, record) =>
          tableDataSource.length >= 1 ? (
            // <Popconfirm title="Sure to delete?" onConfirm={() => handleDelete(record.id)}>
            <div className={`${getDisabledClassName} delete_wrapper`}>
              <img
                onClick={() => {
                  const deletedRow = tableDataSource.find(
                    (x) => x.id === record.id,
                  );
                  const deletedRowIndex = tableDataSource.findIndex(
                    (x) => x.id === record.id,
                  );
                  if (
                    tableDataSource.length - 1 !== deletedRowIndex &&
                    tableDataSource[deletedRowIndex + 1].hierachy >
                      deletedRow.hierachy
                  ) {
                    alert.confirm({
                      onOk: async () => handleDelete(record.id),
                      onCancel: async () => {},
                      okText: 'yes',
                      content: 'Do you want to delele this Node with Children?',
                      noCloseIcon: true,
                      async: true,
                      maskClosable: false,
                    });
                  } else {
                    actionWrapper(() => {
                      handleDelete(record.id);
                    });
                  }
                }}
                src={isLightMode ? deleteLightIcon : deleteIcon}
                className={`${getDisabledClassName} delete_icon`}
              />
            </div>
          ) : // </Popconfirm>
          null,
      });
    }

    return newColumns.map((col) => {
      // console.log('col: ', col);
      if (!col.editable) {
        return col;
      }
      return {
        ...col,
        onCell: (record) => ({
          record,
          type: col.type,
          compProps: col.compProps,
          required: col.required,
          editable: col.editable,
          dataIndex: col.dataIndex,
          title: col.title,
          alwaysEditMode: isEditMode,
          placeholder: col.placeholder,
          disabled: disabledActions,
          handleSave,
          handleHierachyUp,
          handleHierachyDown,
          canHierachyBeDown,
          handleAdd,
          isEditMode,
        }),
      };
    });
  }, [isEditMode, tableColumns, needDelete, disabledActions, tableDataSource]);

  // const SortableItem = sortableElement((props) => (
  //   <tr className="ant-table-row-tr" {...props} />
  // ));
  // const SortableContainer = sortableContainer((props) => <tbody {...props} />);

  const modalTableClassUUID = uuidv4();
  // const getSortableContainer = () =>
  //   document.querySelector('.modal-table' + modalTableClassUUID);

  // const sortableContainerDefaultProps = {
  //   distance: 20,
  //   axis: 'y',
  //   lockAxis: 'y',
  //   useWindowAsScrollContainer: true,
  //   useDragHandle: true,
  //   helperContainer: getSortableContainer,
  // };

  const handleArrayMove = (tableDataSource, oldIndex, newIndex) => {
    let newTableDataSource = [];

    const currentLine = tableDataSource[oldIndex];

    const moveLinesBlock = [currentLine];

    // tableDataSource.filter((v, i, arr) => {
    //   return i > oldIndex && v.hierachy > currentLine.hierachy
    // });

    for (let index = oldIndex + 1; index < tableDataSource.length; index++) {
      if (tableDataSource[index].hierachy <= currentLine.hierachy) {
        break;
      } else {
        moveLinesBlock.push(tableDataSource[index]);
      }
    }
    const otherLines = [
      ...tableDataSource.slice(0, oldIndex),
      ...tableDataSource.slice(oldIndex + moveLinesBlock.length),
    ];

    if (moveLinesBlock.length > 1) {
      newTableDataSource = [...tableDataSource];

      if (newIndex < oldIndex) {
        const leftArray = otherLines.slice(0, newIndex);
        const rightArray = otherLines.slice(newIndex);

        newTableDataSource = [...leftArray, ...moveLinesBlock, ...rightArray];
      }
      if (newIndex > oldIndex + moveLinesBlock.length - 1) {
        const leftArray = otherLines.slice(
          0,
          newIndex - moveLinesBlock.length + 1,
        );
        const rightArray = otherLines.slice(
          newIndex - moveLinesBlock.length + 1,
        );

        newTableDataSource = [...leftArray, ...moveLinesBlock, ...rightArray];
      }
    } else {
      // move one line
      newTableDataSource = arrayMove(tableDataSource, oldIndex, newIndex);
    }

    return newTableDataSource;
  };

  const onSortEnd = (props) => {
    const { oldIndex, newIndex } = props;
    const newTableDataSource = handleArrayMove(
      tableDataSource,
      oldIndex,
      newIndex,
    );
    // const newTableDataSource = arrayMove(tableDataSource, oldIndex, newIndex);
    // Activity cannot be moved to first
    if (
      newTableDataSource[0].hierachy !== 0 ||
      !isCorrectHierachy(newTableDataSource)
    ) {
      return;
    }

    changeData(newTableDataSource);
  };

  const DraggableBodyRow = ({ children, ...props }) => {
    const { attributes, listeners, setNodeRef, transform, transition } =
      useSortable({ id: props['data-row-key'] });

    const style = {
      ...props.style,
      transform: CSS.Transform.toString(transform),
      transition,
    };

    return (
      <tr ref={setNodeRef} style={style} {...attributes} {...listeners}>
        {children}
      </tr>
    );
  };

  const components = {
    body: {
      wrapper: ({ children }) => (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={tableDataSource.map((item) => item.id)}
            strategy={verticalListSortingStrategy}
          >
            <tbody>{children}</tbody>
          </SortableContext>
        </DndContext>
      ),
      row: DraggableBodyRow,
      cell: EditableCell,
    },
  };

  const renderBtnGroups = () =>
    isEditMode ? (
      <div className="btn-group">
        {undo.length ? (
          <FloatingActionButton
            className="undo-btn"
            style={{ marginRight: 6 }}
            onClick={() => {
              const presentState = undo[undo.length - 1];
              // console.log('XXXXpresentState', presentState);
              // console.log('XXXXtableDataSource', tableDataSource);
              setRedo([returnPureDataWithIdValue(tableDataSource), ...redo]);
              setUndo(undo.slice(0, -1));
              changeData(itemWithId(presentState), false);
            }}
          >
            Undo
          </FloatingActionButton>
        ) : (
          <FloatingActionButton
            className="undo-btn disabled"
            style={{ marginRight: 6 }}
          >
            Undo
          </FloatingActionButton>
        )}
        {redo.length ? (
          <FloatingActionButton
            className="redo-btn"
            onClick={() => {
              const presentState = redo[0];
              setUndo([...undo, returnPureDataWithIdValue(tableDataSource)]);
              setRedo(redo.slice(1));
              changeData(itemWithId(presentState), false);
            }}
          >
            Redo
          </FloatingActionButton>
        ) : (
          <FloatingActionButton className="redo-btn disabled">
            Redo
          </FloatingActionButton>
        )}
        <FloatingActionButton
          className="cancel-btn"
          style={{ marginRight: 6 }}
          onClick={() => {
            // 恢复没编辑之前的值
            setTableDataSource(initData);
            setEdittingTableDataSource(initData);
            console.log('defaultDataSource: ', defaultDataSource, initData);
            // 恢复view模式
            setIsEditMode(false);
            setUndo([]);
            setRedo([]);
            afterCancel();
          }}
        >
          Cancel
        </FloatingActionButton>
        <FloatingActionButton
          className="generate-btn"
          style={{ height: 24 }}
          onClick={async () => {
            await form?.validateFields();
            const newArr = handleEditTableData({
              formVal: form.getFieldsValue(),
              tableData: tableDataSource,
            });
            // 在这里把数据暴露出去方便各自调接口
            changeCustmizeData(newArr);
            saveData(returnPureData(newArr));
            changeData(newArr);
            setIsEditMode(false);
          }}
        >
          Generate
        </FloatingActionButton>
      </div>
    ) : (
      <Button
        className="btn role-control edit-btn"
        style={{ height: 26 }}
        onClick={() => setIsEditMode(true)}
      >
        Edit
      </Button>
    );

  return (
    <EditableContext.Provider value={form}>
      <div
        className={`pwc_org_editor_table_wrapper ${
          isEditMode ? 'edit_mode' : null
        }`}
      >
        <div className="pwc_editor_table_header">
          <span>Table</span>
          {renderBtnGroups()}
        </div>
        <Form
          ref={editableRowRef}
          form={form}
          component={false}
          autoComplete="off"
        >
          <Table
            components={components}
            className={`modal-table${modalTableClassUUID}`}
            rowKey="id"
            // rowClassName={() => 'editable-row'}
            bordered
            dataSource={tableDataSource}
            columns={columns}
            locale={{
              emptyText: () => (
                <div
                  className="edit-table-empty"
                  style={{ height: 200, paddingTop: 42 }}
                >
                  <InboxOutlined style={{ fontSize: 50 }} />
                  <p>Select “Edit Visual Data” button to get started</p>
                </div>
              ),
            }}
            {...tableProps}
          />
        </Form>
        {/* 目前快速点击add new 是有问题的，包括export之类的数据都会有些问题 */}
      </div>
    </EditableContext.Provider>
  );
};

export default forwardRef(Index);
